import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isAbort, streamChat, type ChatMessage } from '@/lib/assistant/client';
import type { AssistantPage } from '@/lib/assistant/page-context';
import { assistantPromptQuery, buildMessages, type AssistantBrief } from '@/lib/assistant/prompt';
import type { AssistantSettings } from '@/lib/assistant/settings';

/**
 * The floating assistant's conversation, kept for the session while the shell is mounted. Every
 * turn sends the system prompt, the current page's instructions and brief, then the conversation.
 */

export const EXPLAIN_THIS_PAGE = 'Explain this page';

export interface UserTurn {
  kind: 'user';
  id: number;
  text: string;
  /** Where the turn was asked: a change of page between turns shows as a divider. */
  pathname: string;
  pageTitle: string;
}

export interface AnswerTurn {
  kind: 'answer';
  id: number;
  pathname: string;
  pageTitle: string;
  status: 'streaming' | 'done' | 'stopped' | 'error';
  text: string;
  thinking: boolean;
  finishReason: string | null;
  error: unknown;
  model: string;
  /** The brief this answer was written from: its refs are the only links, its figures the check. */
  brief: AssistantBrief;
  question: string;
}

export type Turn = UserTurn | AnswerTurn;

export interface DividerItem {
  kind: 'divider';
  id: string;
  label: string;
}

/** Earlier turns as chat history: questions and the answers that have text. */
export function toHistory(turns: Turn[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  for (const turn of turns) {
    if (turn.kind === 'user') out.push({ role: 'user', content: turn.text });
    else if ((turn.status === 'done' || turn.status === 'stopped') && turn.text.trim()) {
      out.push({ role: 'assistant', content: turn.text });
    }
  }
  return out;
}

/**
 * The conversation with "Now on: Games" wherever the page changed: between turns asked on
 * different pages, and after the last turn once the user has moved on.
 */
export function withDividers(turns: Turn[], pathname: string, pageTitle: string): (Turn | DividerItem)[] {
  const out: (Turn | DividerItem)[] = [];
  turns.forEach((turn, i) => {
    const before = turns[i - 1];
    if (before && before.pathname !== turn.pathname) {
      out.push({ kind: 'divider', id: `divider-${turn.id}`, label: `Now on: ${turn.pageTitle}` });
    }
    out.push(turn);
  });
  const last = turns[turns.length - 1];
  if (last && last.pathname !== pathname) {
    out.push({ kind: 'divider', id: 'divider-now', label: `Now on: ${pageTitle}` });
  }
  return out;
}

export function useAssistantChat({
  settings,
  page,
  pathname,
  pageTitle,
}: {
  settings: AssistantSettings | null;
  page: AssistantPage | null;
  pathname: string;
  pageTitle: string;
}) {
  const queryClient = useQueryClient();
  const [turns, setTurns] = useState<Turn[]>([]);
  const ids = useRef(0);
  const nextId = useCallback(() => (ids.current += 1), []);
  const running = useRef<AbortController | null>(null);

  useEffect(() => () => running.current?.abort(), []);

  const streaming = turns.some((t) => t.kind === 'answer' && t.status === 'streaming');
  const brief = page?.brief ?? null;
  const canAsk = Boolean(settings && page && brief) && !streaming;

  const update = useCallback((id: number, patch: Partial<AnswerTurn>) => {
    setTurns((prev) => prev.map((t) => (t.kind === 'answer' && t.id === id ? { ...t, ...patch } : t)));
  }, []);

  const run = useCallback(
    async (question: string, base: Turn[]) => {
      if (!settings || !page || !brief) return;
      const controller = new AbortController();
      running.current?.abort();
      running.current = controller;
      const answer: AnswerTurn = {
        kind: 'answer',
        id: nextId(),
        pathname,
        pageTitle,
        status: 'streaming',
        text: '',
        thinking: false,
        finishReason: null,
        error: null,
        model: settings.model,
        brief,
        question,
      };
      setTurns([...base, answer]);
      try {
        const prompt = await queryClient.fetchQuery(assistantPromptQuery);
        const messages = buildMessages({ prompt, pageId: page.id, brief, history: toHistory(base) });
        const result = await streamChat({
          baseUrl: settings.baseUrl,
          model: settings.model,
          messages,
          signal: controller.signal,
          onProgress: ({ text, thinking }) => update(answer.id, { text, thinking }),
        });
        update(answer.id, { status: 'done', text: result.text, thinking: false, finishReason: result.finishReason });
      } catch (error) {
        update(
          answer.id,
          isAbort(error) ? { status: 'stopped', thinking: false } : { status: 'error', error, thinking: false },
        );
      } finally {
        if (running.current === controller) running.current = null;
      }
    },
    [settings, page, brief, pathname, pageTitle, nextId, queryClient, update],
  );

  const ask = useCallback(
    (question: string) => {
      const text = question.trim();
      if (!text || !canAsk) return;
      void run(text, [...turns, { kind: 'user', id: nextId(), text, pathname, pageTitle }]);
    },
    [canAsk, run, turns, nextId, pathname, pageTitle],
  );

  /** The last answer, while the user is still on its page, can be asked again. */
  const last = turns[turns.length - 1];
  const regenerable = last?.kind === 'answer' && last.pathname === pathname ? last : null;
  const regenerate = useCallback(() => {
    if (!canAsk || !regenerable) return;
    void run(regenerable.question, turns.slice(0, -1));
  }, [canAsk, regenerable, run, turns]);

  const stop = useCallback(() => running.current?.abort(), []);

  const clear = useCallback(() => {
    running.current?.abort();
    setTurns([]);
  }, []);

  return { turns, streaming, canAsk, regenerable, ask, regenerate, stop, clear };
}
